import React, { useState, useEffect } from 'react';
import {
  Card,
  TextField,
  Typography,
  Box,
  Divider,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useLanguage } from './LanguageContext';
import { loadRatesData } from '../utils/ratesData';

function CounterExchangeCalculator() {
  const { translation } = useLanguage();
  
  // Currencies
  const [currency1, setCurrency1] = useState('INR');
  const [currency2, setCurrency2] = useState('USD');
  const [availableCurrencies, setAvailableCurrencies] = useState(['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'INR']);
  const [currencies, setCurrencies] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);

  // Rates
  const [buyRate, setBuyRate] = useState('');
  const [sellRate, setSellRate] = useState('');
  const [marketRate, setMarketRate] = useState(null);
  
  // Markup calculations
  const [buyMarkup, setBuyMarkup] = useState(null);
  const [sellMarkup, setSellMarkup] = useState(null);
  const [buyMarkupRating, setBuyMarkupRating] = useState('');
  const [sellMarkupRating, setSellMarkupRating] = useState('');

  // Buy section amounts
  const [buyAmount1, setBuyAmount1] = useState('');
  const [buyAmount2, setBuyAmount2] = useState('');

  // Sell section amounts
  const [sellAmount1, setSellAmount1] = useState('');
  const [sellAmount2, setSellAmount2] = useState('');

  // Function to swap currencies
  const handleSwapCurrencies = () => {
    // Save current values
    const tempCurrency1 = currency1;
    const tempCurrency2 = currency2;
    
    // Swap currencies
    setCurrency1(tempCurrency2);
    setCurrency2(tempCurrency1);
    
    // Clear amounts
    setBuyAmount1('');
    setBuyAmount2('');
    setSellAmount1('');
    setSellAmount2('');
    
    // Clear rates or invert them if needed
    if (buyRate) {
      // When swapping currencies, we need to invert the rate
      setBuyRate((1 / parseFloat(buyRate)).toFixed(4));
    }
    
    if (sellRate) {
      // When swapping currencies, we need to invert the rate
      setSellRate((1 / parseFloat(sellRate)).toFixed(4));
    }
  };

  // Get markup rating based on percentage
  const getMarkupRating = (markupPercentage) => {
    if (markupPercentage <= 1) {
      return {
        text: translation.markupExcellent || 'Excellent',
        color: 'success'
      };
    } else if (markupPercentage <= 2.5) {
      return {
        text: translation.markupGood || 'Good',
        color: 'success'
      };
    } else if (markupPercentage <= 4) {
      return {
        text: translation.markupAcceptable || 'Acceptable',
        color: 'warning'
      };
    } else if (markupPercentage <= 7) {
      return {
        text: translation.markupBad || 'Bad',
        color: 'error'
      };
    } else {
      return {
        text: translation.markupAvoid || 'Avoid',
        color: 'error'
      };
    }
  };

  // Fetch available currencies on component mount
  useEffect(() => {
    fetchStaticData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update calculations when rates or amounts change
  useEffect(() => {
    if (buyAmount1 && buyRate) {
      const calculatedValue = parseFloat(buyAmount1) / parseFloat(buyRate);
      setBuyAmount2(calculatedValue.toString());
    } else {
      setBuyAmount2('');
    }
  }, [buyAmount1, buyRate]);

  useEffect(() => {
    if (sellAmount1 && sellRate) {
      const calculatedValue = parseFloat(sellAmount1) * parseFloat(sellRate);
      setSellAmount2(calculatedValue.toString());
    } else {
      setSellAmount2('');
    }
  }, [sellAmount1, sellRate]);

  // Calculate markup when rates change
  useEffect(() => {
    if (marketRate) {
      calculateMarkups();
    } else {
      fetchMarketRate();
    }
  }, [buyRate, sellRate, marketRate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update market rate when currencies change
  useEffect(() => {
    fetchMarketRate();
  }, [currency1, currency2]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMarketRate = async () => {
    try {
      // First try to get data from window if available
      if (window.exchangeRatesData && window.exchangeRatesData.rates) {
        calculateMarketRateFromGlobal();
      } else {
        const ratesData = await loadRatesData();
        if (ratesData.rates) {
          calculateMarketRateFromGlobal();
        }
      }
    } catch (err) {
      console.error("Error fetching market rate:", err);
    }
  };

  const calculateMarketRateFromGlobal = () => {
    if (window.exchangeRatesData && window.exchangeRatesData.rates) {
      const rates = window.exchangeRatesData.rates;
      const baseCurrency = window.exchangeRatesData.base;
      
      // The exchange rates data usually has USD as the base currency
      // and provides rates for how much of each currency equals 1 USD
      
      let rate;
      
      // For our calculator, we want the rate to represent:
      // How much of currency1 equals 1 unit of currency2
      // Example: if currency1=INR and currency2=USD, we want rate=85 (85 INR = 1 USD)
      
      if (currency1 === baseCurrency) {
        // If currency1 is the base (e.g., USD), then rate is direct from API
        // 1 USD = X currency2
        rate = rates[currency2];
      } else if (currency2 === baseCurrency) {
        // If currency2 is the base (e.g., USD), then we need the inverse
        // Example: INR to USD, if 1 USD = 85 INR, then we need 85
        rate = rates[currency1];
      } else {
        // Cross currency conversion (neither is base currency)
        // Example: GBP to EUR, using USD as base
        // If 1 USD = 0.8 GBP and 1 USD = 0.9 EUR
        // Then 1 EUR = 0.8/0.9 = 0.889 GBP
        const currency1ToBase = rates[currency1]; // How much currency1 per 1 base
        const currency2ToBase = rates[currency2]; // How much currency2 per 1 base
        rate = currency1ToBase / currency2ToBase;
      }
      
      setMarketRate(rate);
      
      // Recalculate markups now that we have the market rate
      if (buyRate || sellRate) {
        calculateMarkups();
      }
    }
  };

  const calculateMarkups = () => {
    if (!marketRate) return;
    if (buyRate && buyRate !== '0') {
      const buyRateValue = parseFloat(buyRate);
      
      // For buy rate calculation, we need to ensure the rates are in the same format
      let buyMarkupPercent;
      
      // This calculation is valid for all currency combinations
      // When buying foreign currency, the customer gets less if buyRate > marketRate
      buyMarkupPercent = ((buyRateValue - marketRate) / marketRate) * 100;
      
      
      // Allow negative values (which indicate better than market rates)
      setBuyMarkup(buyMarkupPercent.toFixed(2));
      setBuyMarkupRating(getMarkupRating(buyMarkupPercent));
    } else {
      setBuyMarkup(null);
      setBuyMarkupRating('');
    }

    if (sellRate && sellRate !== '0') {
      const sellRateValue = parseFloat(sellRate);
      
      // For sell rate calculation, similar logic applies
      let sellMarkupPercent;
      
      // When selling foreign currency, the customer gets less if sellRate < marketRate
      sellMarkupPercent = ((marketRate - sellRateValue) / marketRate) * 100;
      
      
      // Allow negative values (which indicate better than market rates)
      setSellMarkup(sellMarkupPercent.toFixed(2));
      setSellMarkupRating(getMarkupRating(sellMarkupPercent));
    } else {
      setSellMarkup(null);
      setSellMarkupRating('');
    }
  };

  const fetchStaticData = async () => {
    try {
      const ratesData = await loadRatesData();
      if (ratesData.formatted) {
        setLastUpdated(ratesData.formatted);
      }

      if (ratesData.currencies) {
        setAvailableCurrencies(Object.keys(ratesData.currencies));
        setCurrencies(ratesData.currencies);
      }
      
      setError(null);
      
      // Get market rate once we have currencies
      fetchMarketRate();
    } catch (err) {
      console.error("Error fetching static data:", err);
      setError(`${translation.failedToLoadData} ${err.response?.data?.detail || err.message}`);
    }
  };

  // Handle input changes
  const handleBuyRateChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setBuyRate(value);
      if (buyAmount1 && value) {
        // Calculate without formatting during input
        const calculatedValue = parseFloat(buyAmount1) / parseFloat(value);
        setBuyAmount2(calculatedValue.toString());
      } else {
        setBuyAmount2('');
      }
      
      // Make sure market rate is available
      if (!marketRate) {
        fetchMarketRate();
      }
    }
  };

  const handleSellRateChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setSellRate(value);
      if (sellAmount1 && value) {
        // Calculate without formatting during input
        const calculatedValue = parseFloat(sellAmount1) * parseFloat(value);
        setSellAmount2(calculatedValue.toString());
      } else {
        setSellAmount2('');
      }
      
      // Make sure market rate is available
      if (!marketRate) {
        fetchMarketRate();
      }
    }
  };

  const handleBuyAmount1Change = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setBuyAmount1(value);
      if (value && buyRate) {
        const calculatedValue = parseFloat(value) / parseFloat(buyRate);
        setBuyAmount2(calculatedValue.toString());
      } else {
        setBuyAmount2('');
      }
    }
  };

  const handleSellAmount1Change = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setSellAmount1(value);
      if (value && sellRate) {
        const calculatedValue = parseFloat(value) * parseFloat(sellRate);
        setSellAmount2(calculatedValue.toString());
      } else {
        setSellAmount2('');
      }
    }
  };

  const handleBuyAmount2Change = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setBuyAmount2(value);
      if (value && buyRate) {
        // Calculate without formatting during input
        const calculatedValue = parseFloat(value) * parseFloat(buyRate);
        setBuyAmount1(calculatedValue.toString());
      } else {
        setBuyAmount1('');
      }
    }
  };

  const handleSellAmount2Change = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setSellAmount2(value);
      if (value && sellRate) {
        // Calculate without formatting during input
        const calculatedValue = parseFloat(value) / parseFloat(sellRate);
        setSellAmount1(calculatedValue.toString());
      } else {
        setSellAmount1('');
      }
    }
  };

  // Format input fields when the user is done typing
  const handleBuyAmountBlur = (setter, value) => {
    if (value) {
      const formattedValue = parseFloat(value).toFixed(2);
      setter(formattedValue);
    }
  };

  const handleSellAmountBlur = (setter, value) => {
    if (value) {
      const formattedValue = parseFloat(value).toFixed(2);
      setter(formattedValue);
    }
  };

  const handleRateBlur = (setter, value) => {
    if (value) {
      const formattedValue = parseFloat(value).toFixed(4); // Use 4 decimal places for exchange rates
      setter(formattedValue);
    }
  };

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: 700,
        height: 'auto',
        minHeight: { xs: 'auto', sm: 300 },
        borderRadius: { xs: 4, sm: 8 },
        border: '1px solid rgba(0, 0, 0, 0.28)',
        p: { xs: 2, sm: 4 },
        backgroundColor: 'white',
        boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.5)',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {lastUpdated && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
          {translation.lastUpdated}: {lastUpdated} {translation.utc}
        </Box>
      )}
      
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          {translation.counterRates}
        </Typography>
        <Tooltip title={translation.swapCurrencies}>
          <IconButton onClick={handleSwapCurrencies}>
            <SwapHorizIcon />
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Currency Selector */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Select
          value={currency1}
          onChange={(e) => setCurrency1(e.target.value)}
          sx={{ width: '48%' }}
        >
          {availableCurrencies.map((currency) => (
            <MenuItem key={currency} value={currency}>
              {currency} - {currencies[currency]}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={currency2}
          onChange={(e) => setCurrency2(e.target.value)}
          sx={{ width: '48%' }}
        >
          {availableCurrencies.map((currency) => (
            <MenuItem key={currency} value={currency}>
              {currency} - {currencies[currency]}
            </MenuItem>
          ))}
        </Select>
      </Box>
      
      {/* Buy section */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        {translation.buy} {currency2}
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ width: '48%' }}>
          <Typography variant="body2">{translation.buyRate}</Typography>
          <TextField
            value={buyRate}
            onChange={handleBuyRateChange}
            onBlur={() => handleRateBlur(setBuyRate, buyRate)}
            placeholder="0.00"
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          />
          {buyMarkup !== null && buyMarkupRating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption">
                {translation.markupLabel}: {buyMarkup}%
              </Typography>
              <Chip 
                label={buyMarkupRating.text} 
                color={buyMarkupRating.color} 
                size="small" 
                sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: '20px' }}
              />
            </Box>
          )}
        </Box>
        
        <Box sx={{ width: '48%' }}>
          <Typography variant="body2">{translation.youPay} ({currency1})</Typography>
          <TextField
            value={buyAmount1}
            onChange={handleBuyAmount1Change}
            onBlur={() => handleBuyAmountBlur(setBuyAmount1, buyAmount1)}
            placeholder="0.00"
            fullWidth
            size="small"
          />
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Box sx={{ width: '48%' }}>
          <Typography variant="body2">{translation.youGet} ({currency2})</Typography>
          <TextField
            value={buyAmount2}
            onChange={handleBuyAmount2Change}
            onBlur={() => handleBuyAmountBlur(setBuyAmount2, buyAmount2)}
            placeholder="0.00"
            fullWidth
            size="small"
          />
        </Box>
      </Box>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Sell section */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        {translation.sell} {currency2}
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ width: '48%' }}>
          <Typography variant="body2">{translation.sellRate}</Typography>
          <TextField
            value={sellRate}
            onChange={handleSellRateChange}
            onBlur={() => handleRateBlur(setSellRate, sellRate)}
            placeholder="0.00"
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          />
          {sellMarkup !== null && sellMarkupRating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption">
                {translation.markupLabel}: {sellMarkup}%
              </Typography>
              <Chip 
                label={sellMarkupRating.text} 
                color={sellMarkupRating.color} 
                size="small" 
                sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: '20px' }}
              />
            </Box>
          )}
        </Box>
        
        <Box sx={{ width: '48%' }}>
          <Typography variant="body2">{translation.youPay} ({currency2})</Typography>
          <TextField
            value={sellAmount1}
            onChange={handleSellAmount1Change}
            onBlur={() => handleSellAmountBlur(setSellAmount1, sellAmount1)}
            placeholder="0.00"
            fullWidth
            size="small"
          />
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Box sx={{ width: '48%' }}>
          <Typography variant="body2">{translation.youGet} ({currency1})</Typography>
          <TextField
            value={sellAmount2}
            onChange={handleSellAmount2Change}
            onBlur={() => handleSellAmountBlur(setSellAmount2, sellAmount2)}
            placeholder="0.00"
            fullWidth
            size="small"
          />
        </Box>
      </Box>
    </Card>
  );
}

export default CounterExchangeCalculator; 
